import React, { ChangeEvent, FC, useState } from 'react';
import { Box, Button, FormControl, Grid, TextField } from '@mui/material';
import { MaterialMacro } from 'models/material-macro';
import { ApolloCache, Reference, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { createMaterialMacroTemplate } from 'apollo/templates/material-macro';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type AddMaterialMacroDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onAddMaterialMacro: (materialMacro: MaterialMacro) => void;
    materialMacros: MaterialMacro[];
};
const AddMaterialMacroDialog: FC<AddMaterialMacroDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    onAddMaterialMacro,
    materialMacros,
}: AddMaterialMacroDialogProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow']);
    const [materialMacroName, setMaterialMacroName] = useState('');
    const [materialMacroNameError, setMaterialMacroNameError] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [createMaterialMacro] = useMutation(createMaterialMacroTemplate, {
        variables: {
            name: materialMacroName,
        },
        onCompleted: ({ createMaterialMacro }) => {
            onAddMaterialMacro(createMaterialMacro);
            onCloseDialog();
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<MaterialMacro>, { data: { createMaterialMacro } }) {
            cache.modify({
                fields: {
                    materialMacros(macroRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (macroRefs instanceof Array) {
                            return [...macroRefs, toReference(createMaterialMacro)];
                        }
                        return [macroRefs, toReference(createMaterialMacro)];
                    },
                },
            });
        },
    });

    const handleMaterialMacroNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setMaterialMacroNameError(false);
        if (!event.target.value || materialMacroNameAlreadyExist(event.target.value)) {
            setMaterialMacroNameError(true);
        }
        setMaterialMacroName(event.target.value);
    };

    const materialMacroNameAlreadyExist = (name: string) => {
        const materialMacroNames = materialMacros.map((materialMacro) =>
            materialMacro.name.replace(/\s/g, '').toLocaleLowerCase(),
        );
        return materialMacroNames.includes(name.replace(/\s/g, '').toLocaleLowerCase());
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={dialogOpen}
            onClose={onCloseDialog}
            title={translate('lab-settings:ADD_MATERIAL_MACRO')}
            actions={
                <Button
                    variant="contained"
                    disabled={!materialMacroName || materialMacroNameError}
                    onClick={() => createMaterialMacro()}
                >
                    {translate('lab-settings:SAVE_MATERIAL_MACRO')}
                </Button>
            }
        >
            <Box mt={1} mb={2}>
                <Grid container spacing={2}>
                    <Grid item md={8} sm={12}>
                        <FormControl fullWidth>
                            <TextField
                                id="material-macro-name"
                                label={translate('lab-settings:MATERIAL_MACRO_NAME')}
                                required
                                onChange={handleMaterialMacroNameChange}
                                error={materialMacroNameError}
                                helperText={
                                    materialMacroNameError && !materialMacroName
                                        ? translate('workflow:VALIDATION:NAME')
                                        : materialMacroNameError &&
                                          translate('lab-settings:VALIDATION:MATERIAL_MACRO_ALREADY_EXIST')
                                }
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default AddMaterialMacroDialog;
